// ReadReceipts — an avatar stack rendered on the right edge of a
// message showing the users who have read up to this event.
//
// Pure projection: takes a list of `Receipt`s (own receipt already
// excluded by `getReceiptsForEvent`) and renders up to three 16×16
// circular avatars with a 6px horizontal overlap. More than three
// readers collapse into a trailing `+N` chip.
//
// The brief pins the geometry exactly; values that the brief names
// (16×16 avatars, 6px overlap, 1px halo, 8px right inset) are inlined
// literally. Nothing here is negotiable — the stack must read cleanly
// on every density and against the hover-raised row background.

import { useContext, type CSSProperties } from "react";

import { MatrixContext } from "~/context/MatrixContext";
import { useAuthenticatedMedia } from "~/hooks/useAuthenticatedMedia";
import { matrixLocalpart } from "~/lib/display-name";
import { getInitials } from "~/lib/initials";
import type { Receipt } from "~/lib/matrix";

export interface ReadReceiptsProps {
  /** Receipts to display — ordered newest-first by the caller. */
  receipts: Receipt[];
  /**
   * Resolve a user id to a display name for initials + tooltip. The
   * embedder (MessageBubble) sources this from the SDK's member map.
   * Unresolved ids fall back to a single-char initial of the Matrix
   * id's localpart — the avatar is still recognisable against the
   * list order even without a name.
   */
  resolveName: (userId: string) => string;
  /**
   * Optional `mxc://` URI resolver for the receipt avatar. Returning
   * null renders the initials fallback; returning an `mxc://` URI
   * routes through `useAuthenticatedMedia` to a blob URL the inline
   * `backgroundImage` can load. Non-`mxc://` strings (legacy
   * pre-resolved URLs) collapse to the initials path — the migration
   * to authenticated v1.11 media (#3147 / #3171) intentionally
   * removes the bypass so URL-revocation stays consistent across
   * consumers.
   *
   * Defaults to "no resolver" → initials, so stories don't need to
   * stub a URL map.
   */
  resolveAvatar?: (userId: string) => string | null;
}

const AVATAR_SIZE = 16;
const OVERLAP = 6;
const MAX_VISIBLE = 3;

export function ReadReceipts({ receipts, resolveName, resolveAvatar }: ReadReceiptsProps) {
  if (receipts.length === 0) return null;
  const visible = receipts.slice(0, MAX_VISIBLE);
  const overflow = receipts.length - visible.length;
  return (
    <span
      aria-label={`Read by ${receipts.length}`}
      style={{
        display: "inline-flex",
        alignItems: "center",
        // 8px right inset per brief. Left inset stays at 0 so the
        // stack hugs the right edge of the message body column.
        marginRight: "var(--chat-space-2)",
        // Ensure the stack sits above the hover-action gutter when
        // both are present — receipts are informational and should
        // not steal pointer events.
        pointerEvents: "none",
        flexShrink: 0,
      }}
    >
      {visible.map((receipt, i) => (
        <ReceiptAvatar
          key={receipt.userId}
          receipt={receipt}
          name={resolveName(receipt.userId)}
          avatarUrl={resolveAvatar?.(receipt.userId) ?? null}
          // First receipt on top — give it the highest z-index so
          // overlap reads left-over-right.
          zIndex={visible.length - i}
          // Overlap: the first avatar carries no negative margin; every
          // subsequent avatar shifts left by `AVATAR_SIZE - OVERLAP`
          // so the exposed slice is exactly `OVERLAP` pixels wide
          // (brief-pinned 6px).
          marginLeft={i === 0 ? 0 : -(AVATAR_SIZE - OVERLAP)}
        />
      ))}
      {overflow > 0 ? <OverflowChip count={overflow} visibleAvatars={visible.length} /> : null}
    </span>
  );
}

interface ReceiptAvatarProps {
  receipt: Receipt;
  name: string;
  avatarUrl: string | null;
  zIndex: number;
  marginLeft: number;
}

function ReceiptAvatar({ receipt, name, avatarUrl, zIndex, marginLeft }: ReceiptAvatarProps) {
  const initials = getInitials(name) || fallbackInitial(receipt.userId);
  const tooltip = `${name} · ${formatReceiptTs(receipt.ts)}`;
  // Synapse ≥ 1.120's `enable_authenticated_media: true` rejects the
  // unauthenticated v3 download URL the previous CSS background-image
  // path relied on. Route `mxc://` URIs through the SDK via
  // `useAuthenticatedMedia` so the rendered href is a blob URL the
  // browser can load directly. The hook short-circuits when `client`
  // or the canonicalised mxc input is null, which keeps Storybook
  // (no MatrixProvider) and resolver-less callers on the initials
  // path. Each instance owns its own object URL — no cross-component
  // cache, per the #3147 design.
  const matrix = useContext(MatrixContext);
  const client = matrix?.client ?? null;
  const avatarMxc = avatarUrl && avatarUrl.startsWith("mxc://") ? avatarUrl : null;
  const resolvedHref = useAuthenticatedMedia(client, avatarMxc).src;
  const base: CSSProperties = {
    position: "relative",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: AVATAR_SIZE,
    height: AVATAR_SIZE,
    borderRadius: "var(--chat-radius-full)",
    // 1px halo in `--chat-surface-app` so adjacent avatars read as
    // separate stacked chips even against the timeline's raised
    // hover background. The brief pins the halo colour explicitly.
    border: "1px solid var(--chat-surface-app)",
    boxSizing: "content-box",
    background: "var(--chat-surface-raised)",
    color: "var(--chat-fg-secondary)",
    fontFamily: "var(--chat-font-sans)",
    fontSize: 9,
    fontWeight: 500,
    lineHeight: 1,
    zIndex,
    marginLeft,
    // The brief calls for a hover tooltip with display name + timestamp.
    // A native `title` is the cheapest path that still satisfies the
    // keyboard-focus "hover any avatar" rule. A richer popover would
    // duplicate the surface the emoji/more popovers already own; the
    // title attribute is deliberately restrained.
    pointerEvents: "auto",
  };
  if (resolvedHref) {
    return (
      <span
        title={tooltip}
        style={{
          ...base,
          background: "var(--chat-surface-raised)",
          // `JSON.stringify` quote-escapes the resolved URL so a future
          // blob URL with quotes / commas can't break out of the inline
          // style declaration.
          backgroundImage: `url(${JSON.stringify(resolvedHref)})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
    );
  }
  // Initials fallback while the avatar fetch is in flight, or when
  // no avatar is available at all. Mirrors `RoomListItem.DmAvatar`'s
  // placeholder branch.
  return (
    <span title={tooltip} style={base}>
      {initials}
    </span>
  );
}

function OverflowChip({ count, visibleAvatars }: { count: number; visibleAvatars: number }) {
  return (
    <span
      aria-label={`${count} more`}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        height: AVATAR_SIZE,
        minWidth: AVATAR_SIZE,
        padding: "0 var(--chat-space-1)",
        marginLeft: -(AVATAR_SIZE - OVERLAP),
        borderRadius: "var(--chat-radius-full)",
        background: "var(--chat-surface-raised)",
        color: "var(--chat-fg-secondary)",
        fontFamily: "var(--chat-font-sans)",
        fontSize: "var(--chat-type-body-xs-size)",
        lineHeight: "var(--chat-type-body-xs-line)",
        fontWeight: 500,
        // The chip overlaps the rightmost avatar's right edge — it
        // must sit *above* every visible avatar (which carry indices
        // 1..visibleAvatars) so the `+N` text isn't clipped.
        zIndex: visibleAvatars + 1,
        pointerEvents: "auto",
      }}
    >
      +{count}
    </span>
  );
}

/**
 * Fallback initial when the display name is empty. Reaches into the
 * Matrix id's localpart (`@alice:server` → `A`) so the avatar is
 * still a single readable glyph rather than a blank disc.
 */
function fallbackInitial(userId: string): string {
  const localpart = matrixLocalpart(userId);
  return (localpart[0] ?? "?").toUpperCase();
}

/** Format a receipt timestamp as "HH:MM" for the tooltip. Absolute
 *  time is more useful than relative — a receipt's precise minute
 *  carries forensic value when the user hovers. */
function formatReceiptTs(ts: number): string {
  if (!Number.isFinite(ts) || ts <= 0) return "";
  const d = new Date(ts);
  const hh = d.getHours().toString().padStart(2, "0");
  const mm = d.getMinutes().toString().padStart(2, "0");
  return `${hh}:${mm}`;
}
